import { Avatar } from "./avatar";
import { FallingIcon } from "./icon";
import { AVATAR_URL, pickRandomIcon } from "./assets";

const SPAWN_INTERVAL_MS = 1500;
const MAX_ACTIVE_ICONS = 2;
const CATCH_RADIUS = 60; // px — generous: ~1.5x avatar half-width

// Lerp speed for desktop hover enter + exit.
// At 60fps: ~15 frames to cover 90% of distance (~250ms feel).
const DESKTOP_LERP = 0.15;

// Lerp speed for mobile autonomous movement.
// Must guarantee a catch before the icon exits the container.
// At FALL_SPEED=1.3px/frame over 300px container: ~230 frames of fall time.
// After 40 frames at 0.12: covers 98%+ of any column-to-column distance.
const MOBILE_LERP = 0.12;

// Mobile spawn columns as fractions of container width.
// Round-robin: left (25%) → center (50%) → right (75%) → repeat.
// Predictable L→C→R movement makes the avatar readable as purposeful.
const MOBILE_COLUMNS = [0.25, 0.5, 0.75];

export interface IOGameOptions {
  // forceMobile: override pointer detection — scripted column loop, no user interaction.
  // Used in sandbox "Simulate Mobile" toggle.
  forceMobile?: boolean;
  // touchInteractive: finger drives the avatar, same lerp mechanic as desktop hover.
  // Mutually exclusive with forceMobile — touchInteractive takes precedence.
  touchInteractive?: boolean;
  // onStateChange: called whenever state changes — for sandbox debug overlay
  onStateChange?: (state: IOGameState) => void;
}

export type IOGameState = "hidden" | "passive" | "tracking" | "touch-active" | "mobile-loop";

const STYLES = `
  .io-avatar {
    will-change: left;
  }

  @keyframes io-pulse {
    0%   { transform: scale(1); }
    50%  { transform: scale(1.15); }
    100% { transform: scale(1); }
  }

  @keyframes io-pulse-special {
    0%   { transform: scale(1);    filter: drop-shadow(0 0  0px transparent); }
    40%  { transform: scale(1.28); filter: drop-shadow(0 0 18px rgba(255, 220, 60, 1))
                                           drop-shadow(0 0 36px rgba(80, 200, 255, 0.85)); }
    100% { transform: scale(1);    filter: drop-shadow(0 0  0px transparent); }
  }

  .io-avatar--pulse {
    animation: io-pulse 0.32s ease-out;
  }

  .io-avatar--pulse-special {
    animation: io-pulse-special 0.6s ease-out;
  }

  @keyframes io-icon-catch {
    0%   { transform: scale(1);   opacity: 1; }
    40%  { transform: scale(1.4); opacity: 0.9; }
    100% { transform: scale(0.1); opacity: 0; }
  }

  .io-icon--caught {
    animation: io-icon-catch 0.36s ease-out forwards;
  }
`;

export class IOGame {
  private readonly container: HTMLElement;
  private readonly avatar: Avatar;
  private readonly isMobile: boolean;
  private readonly isTouchInteractive: boolean;
  private readonly onStateChange?: (state: IOGameState) => void;

  private icons: FallingIcon[] = [];

  // loopRunning: true when card is in viewport (IntersectionObserver fired).
  // false when off-screen — rAF and spawning are fully paused to save cycles.
  private loopRunning = false;

  // isTracking: true while mouse is over the card (desktop only).
  // Switches the avatar lerp target from center → cursor position.
  // Has no effect in mobile mode.
  private isTracking = false;

  private animFrameId = 0;
  private spawnTimerId = 0;

  // Mobile: index into MOBILE_COLUMNS — advances on each spawn for L→C→R→L pattern.
  private mobileColumnIndex = 0;
  private lastIcon: import("./assets").IconAsset | undefined = undefined;

  constructor(container: HTMLElement, opts: IOGameOptions = {}) {
    this.container = container;
    this.onStateChange = opts.onStateChange;

    // touchInteractive takes precedence over forceMobile.
    // pointer: fine = mouse device (desktop). pointer: coarse = touch (mobile).
    this.isTouchInteractive = opts.touchInteractive ?? false;
    this.isMobile = !this.isTouchInteractive && (opts.forceMobile ?? !window.matchMedia("(pointer: fine)").matches);

    container.style.position = "relative";
    container.style.overflow = "hidden";
    this.injectStyles();

    this.avatar = new Avatar(AVATAR_URL, container.clientWidth);
    container.appendChild(this.avatar.el);

    // IntersectionObserver controls the loop lifecycle.
    this.bindIntersectionObserver();
    if (this.isTouchInteractive) {
      // Touch-interactive: finger drives the avatar.
      // touch-action:none prevents the browser scroll/pan gesture inside the container.
      container.style.touchAction = "none";
      this.bindTouchEvents();
    } else if (!this.isMobile) {
      this.bindMouseEvents();
    }
  }

  private get w(): number { return this.container.clientWidth; }
  private get h(): number { return this.container.clientHeight; }

  private injectStyles(): void {
    if (document.getElementById("io-game-styles")) return;
    const style = document.createElement("style");
    style.id = "io-game-styles";
    style.textContent = STYLES;
    document.head.appendChild(style);
  }

  private bindIntersectionObserver(): void {
    // threshold: 0.1 — loop starts when 10% of card is visible.
    // This prevents wasted rAF on cards that are barely peeking into view.
    const observer = new IntersectionObserver((entries) => {
      entries[0].isIntersecting ? this.startLoop() : this.stopLoop();
    }, { threshold: 0.1 });
    observer.observe(this.container);
  }

  private bindMouseEvents(): void {
    this.container.addEventListener("mouseenter", this.onEnter);
    this.container.addEventListener("mouseleave", this.onLeave);
    this.container.addEventListener("mousemove", this.onMove);
  }

  private bindTouchEvents(): void {
    this.container.addEventListener("touchstart", this.onTouchStart, { passive: false });
    this.container.addEventListener("touchmove", this.onTouchMove, { passive: false });
    this.container.addEventListener("touchend", this.onTouchEnd);
    this.container.addEventListener("touchcancel", this.onTouchEnd);
  }

  private startLoop(): void {
    if (this.loopRunning) return;
    this.loopRunning = true;
    this.startSpawning();
    this.tick();
    this.emitState();
  }

  private stopLoop(): void {
    if (!this.loopRunning) return;
    this.loopRunning = false;

    // Reset tracking — cursor may no longer be over the card after re-entry.
    this.isTracking = false;

    clearInterval(this.spawnTimerId);
    cancelAnimationFrame(this.animFrameId);

    // Remove all in-flight icons so they don't persist as orphan DOM nodes.
    this.icons.forEach(i => i.el.remove());
    this.icons = [];

    // Hard reset avatar to center — fresh state on next viewport entry.
    this.avatar.setInstant(this.w / 2, this.w);
    this.emitState();
  }

  // Desktop mouseenter: begin lerping avatar toward cursor.
  // No activation delay — tracking starts immediately, lerp smooths the first movement.
  private onEnter = (): void => {
    this.isTracking = true;
    this.emitState();
  };

  // Desktop mouseleave: stop tracking, lerp avatar back to center.
  private onLeave = (e: MouseEvent): void => {
    // .arrows sits outside #io-container but overlaps it visually — ignore that as a leave.
    if ((e.relatedTarget as Element | null)?.closest?.(".arrows")) return;
    this.isTracking = false;
    // Set center as the new lerp target — avatar drifts back smoothly.
    this.avatar.lerpToX(this.w / 2, this.w);
    this.emitState();
  };

  // Desktop mousemove: update lerp target to cursor position every frame.
  private onMove = (e: MouseEvent): void => {
    if (!this.isTracking) return;
    const rect = this.container.getBoundingClientRect();
    this.avatar.lerpToX(e.clientX - rect.left, this.w);
  };

  // Touch start: begin tracking finger position.
  private onTouchStart = (e: TouchEvent): void => {
    e.preventDefault();
    this.isTracking = true;
    const rect = this.container.getBoundingClientRect();
    this.avatar.lerpToX(e.touches[0].clientX - rect.left, this.w);
    this.emitState();
  };

  // Touch move: update lerp target to finger position.
  // preventDefault() stops the browser panning/scrolling the page while interacting.
  private onTouchMove = (e: TouchEvent): void => {
    e.preventDefault();
    if (!this.isTracking) return;
    const rect = this.container.getBoundingClientRect();
    this.avatar.lerpToX(e.touches[0].clientX - rect.left, this.w);
  };

  // Touch end/cancel: stop tracking, lerp avatar back to center.
  private onTouchEnd = (): void => {
    this.isTracking = false;
    this.avatar.lerpToX(this.w / 2, this.w);
    this.emitState();
  };

  private startSpawning(): void {
    clearInterval(this.spawnTimerId);
    this.spawnIcon();
    this.spawnTimerId = window.setInterval(() => {
      if (this.icons.length < MAX_ACTIVE_ICONS) this.spawnIcon();
    }, SPAWN_INTERVAL_MS);
  }

  private spawnIcon(): void {
    if (this.isMobile) {
      // Mobile: predetermined column X — tells the avatar exactly where to move.
      // Avatar lerp target is set immediately on spawn, giving maximum travel time.
      const col = MOBILE_COLUMNS[this.mobileColumnIndex % MOBILE_COLUMNS.length];
      this.mobileColumnIndex++;
      const colX = this.w * col;
      const asset = pickRandomIcon(this.lastIcon);
      this.lastIcon = asset;
      const icon = new FallingIcon(asset, this.w, colX);
      this.avatar.lerpToX(colX, this.w);
      this.icons.push(icon);
      this.container.appendChild(icon.el);
    } else {
      // Desktop: random X — avatar doesn't move toward it in passive mode.
      // In tracking mode the avatar is cursor-driven, not icon-driven.
      const asset = pickRandomIcon(this.lastIcon);
      this.lastIcon = asset;
      const icon = new FallingIcon(asset, this.w);
      this.icons.push(icon);
      this.container.appendChild(icon.el);
    }
  }

  private tick = (): void => {
    if (!this.loopRunning) return;

    const containerHeight = this.h;
    const avatarTop = this.avatar.topEdge(containerHeight);

    // Advance avatar lerp every frame regardless of mode.
    //   desktop passive      → target is center (no movement unless tracking)
    //   desktop tracking     → target is cursor X (updated by onMove)
    //   touch interactive    → target is finger X (updated by onTouchMove)
    //   mobile scripted loop → target is current column X (set in spawnIcon)
    this.avatar.update(this.isMobile ? MOBILE_LERP : DESKTOP_LERP);

    const avatarCenterX = this.avatar.centerX;

    this.icons = this.icons.filter((icon) => {
      icon.update();

      if (icon.y > containerHeight) {
        icon.exit();
        return false;
      }

      // Catch check runs in all modes — passive desktop catches icons that happen
      // to fall over the centered avatar. Mobile always catches via column alignment.
      const inRange =
        Math.abs(icon.centerX - avatarCenterX) < CATCH_RADIUS &&
        icon.bottom >= avatarTop;

      if (inRange) {
        icon.catch();
        this.avatar.pulse(icon.isSpecial);
        return false;
      }

      return true;
    });

    this.animFrameId = requestAnimationFrame(this.tick);
  };

  private emitState(): void {
    if (!this.onStateChange) return;
    if (!this.loopRunning) {
      this.onStateChange("hidden");
    } else if (this.isMobile) {
      this.onStateChange("mobile-loop");
    } else if (this.isTouchInteractive && this.isTracking) {
      this.onStateChange("touch-active");
    } else if (this.isTracking) {
      this.onStateChange("tracking");
    } else {
      this.onStateChange("passive");
    }
  }

  destroy(): void {
    clearInterval(this.spawnTimerId);
    cancelAnimationFrame(this.animFrameId);
    this.container.removeEventListener("mouseenter", this.onEnter);
    this.container.removeEventListener("mouseleave", this.onLeave);
    this.container.removeEventListener("mousemove", this.onMove);
    this.container.removeEventListener("touchstart", this.onTouchStart);
    this.container.removeEventListener("touchmove", this.onTouchMove);
    this.container.removeEventListener("touchend", this.onTouchEnd);
    this.container.removeEventListener("touchcancel", this.onTouchEnd);
    this.avatar.el.remove();
    this.icons.forEach((i) => i.el.remove());
    this.icons = [];
  }
}
